import React, { lazy, Suspense } from 'react';

import { Navigate, Route, Routes } from 'react-router-dom';

import {
  CLIENT_BOARD_ROUTE_PATH,
  CLIENT_CANDIDATE_DETAIL_ROUTE_PATH,
  CLIENT_ROUTE_PATH,
} from 'pages/client/client-routes';
import { JobsDetailLoading } from 'pages/public/jobs/jobs-detail-loading';
import { JobsListLoading } from 'pages/public/jobs-list/jobs-list-loading';
import { LandingLoading } from 'pages/public/landing/landing-loading';
import { PageLoadingSpinner } from 'shared-components/page-loading-spinner';

const OidcCallbackPage = lazy(() => import('auth/oidc-callback-page'));

const AdminPage = lazy(() => import('pages/admin'));

const AdminProtectedRoute = lazy(
  () => import('pages/admin/admin-protected-route')
);

const ClientProtectedRoute = lazy(
  () => import('pages/client/client-protected-route')
);

const ClientJobsListPage = lazy(() => import('pages/client/client-jobs-list'));

const ClientCompanyProfilePage = lazy(
  () => import('pages/client/client-company-profile')
);

const ClientJobBoardLayout = lazy(
  () => import('pages/client/client-job-board-layout')
);

const ClientCandidateDetailModal = lazy(
  () => import('pages/client/client-candidate-detail-modal')
);

const ClientsPage = lazy(() => import('pages/admin/clients'));

const CreateClientPage = lazy(
  () => import('pages/admin/clients/create-client-page')
);

const ClientDetailPage = lazy(
  () => import('pages/admin/clients/client-detail-page')
);

const JobIntakesPage = lazy(() => import('pages/admin/job-intakes'));

const CreateJobIntakePage = lazy(
  () => import('pages/admin/job-intakes/create-job-intake-page')
);

const EditJobIntakePage = lazy(
  () => import('pages/admin/job-intakes/edit-job-intake-page')
);

const JobIntakeDetailPage = lazy(
  () => import('pages/admin/job-intakes/job-intake-detail')
);

const JobsPage = lazy(() => import('pages/admin/jobs'));

const JobWorkspace = lazy(() => import('pages/admin/jobs/job-workspace'));

const JobApplicationsTab = lazy(
  () => import('pages/admin/jobs/job-applications-tab')
);

const JobPostTab = lazy(() => import('pages/admin/jobs/job-post-tab'));

const AiInterviewTab = lazy(() => import('pages/admin/jobs/ai-interview-tab'));

const PreScreeningTab = lazy(
  () => import('pages/admin/jobs/pre-screening-tab')
);

const ApplicationsPage = lazy(() => import('pages/admin/applications'));

const ApplicationDetailPage = lazy(
  () => import('pages/admin/applications/application-detail')
);

const CreateJobPostPage = lazy(
  () => import('pages/admin/jobs/create-job-post-page')
);

const EditJobPostPage = lazy(
  () => import('pages/admin/jobs/edit-job-post-page')
);

const PublicJobsListPage = lazy(() => import('pages/public/jobs-list'));

const PublicJobDetailPage = lazy(() => import('pages/public/jobs'));

const LandingPage = lazy(() => import('pages/public/landing'));
const NotFoundPage = lazy(() => import('pages/not-found'));

const withPageLoadingFallback = (element: React.ReactElement) => (
  <Suspense fallback={<PageLoadingSpinner />}>{element}</Suspense>
);

const AppRoutes: React.FC = () => (
  <Routes>
    <Route
      path="/"
      element={
        <Suspense fallback={<LandingLoading />}>
          <LandingPage />
        </Suspense>
      }
    />

    <Route
      path="/auth/callback"
      element={withPageLoadingFallback(<OidcCallbackPage />)}
    />

    <Route
      path="/admin"
      element={
        <Suspense fallback={<PageLoadingSpinner />}>
          <AdminProtectedRoute />
        </Suspense>
      }
    >
      <Route index element={withPageLoadingFallback(<AdminPage />)} />

      <Route path="clients">
        <Route index element={withPageLoadingFallback(<ClientsPage />)} />

        <Route
          path="new"
          element={withPageLoadingFallback(<CreateClientPage />)}
        />

        <Route
          path=":companyId"
          element={withPageLoadingFallback(<ClientDetailPage />)}
        />
      </Route>

      <Route path="job-intakes">
        <Route index element={withPageLoadingFallback(<JobIntakesPage />)} />

        <Route
          path="new"
          element={withPageLoadingFallback(<CreateJobIntakePage />)}
        />

        <Route
          path=":jobIntakeId"
          element={withPageLoadingFallback(<JobIntakeDetailPage />)}
        />

        <Route
          path=":jobIntakeId/edit"
          element={withPageLoadingFallback(<EditJobIntakePage />)}
        />
      </Route>

      <Route path="jobs">
        <Route index element={withPageLoadingFallback(<JobsPage />)} />

        <Route
          path="new/:jobIntakeId"
          element={withPageLoadingFallback(<CreateJobPostPage />)}
        />

        <Route
          path=":jobId"
          element={withPageLoadingFallback(<JobWorkspace />)}
        >
          <Route element={withPageLoadingFallback(<JobApplicationsTab />)}>
            <Route index element={null} />

            <Route
              path="applications/:applicationId"
              element={withPageLoadingFallback(<ApplicationDetailPage />)}
            />
          </Route>

          <Route
            path="job-post"
            element={withPageLoadingFallback(<JobPostTab />)}
          />

          <Route
            path="ai-interview"
            element={withPageLoadingFallback(<AiInterviewTab />)}
          />

          <Route
            path="pre-screening"
            element={withPageLoadingFallback(<PreScreeningTab />)}
          />
        </Route>

        <Route
          path=":jobId/edit"
          element={withPageLoadingFallback(<EditJobPostPage />)}
        />
      </Route>

      <Route
        path="applications"
        element={withPageLoadingFallback(<ApplicationsPage />)}
      >
        <Route
          path=":applicationId"
          element={withPageLoadingFallback(<ApplicationDetailPage />)}
        />
      </Route>

      <Route path="*" element={withPageLoadingFallback(<NotFoundPage />)} />
    </Route>

    <Route
      path={CLIENT_ROUTE_PATH}
      element={withPageLoadingFallback(<ClientProtectedRoute />)}
    >
      <Route index element={<Navigate replace to="/client/jobs" />} />

      <Route
        path="jobs"
        element={withPageLoadingFallback(<ClientJobsListPage />)}
      />

      <Route
        path="company-profile"
        element={withPageLoadingFallback(<ClientCompanyProfilePage />)}
      />

      <Route
        path={CLIENT_BOARD_ROUTE_PATH}
        element={withPageLoadingFallback(<ClientJobBoardLayout />)}
      >
        <Route index element={null} />

        <Route
          path={CLIENT_CANDIDATE_DETAIL_ROUTE_PATH}
          element={withPageLoadingFallback(<ClientCandidateDetailModal />)}
        />
      </Route>

      <Route path="*" element={withPageLoadingFallback(<NotFoundPage />)} />
    </Route>

    <Route
      path="/jobs"
      element={
        <Suspense fallback={<JobsListLoading />}>
          <PublicJobsListPage />
        </Suspense>
      }
    />

    <Route
      path="/jobs/:slug"
      element={
        <Suspense fallback={<JobsDetailLoading />}>
          <PublicJobDetailPage />
        </Suspense>
      }
    />

    <Route path="*" element={withPageLoadingFallback(<NotFoundPage />)} />
  </Routes>
);

export { AppRoutes };
